import { normalizedUuidSchema } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { useGuestQuoteRequestFormQuery } from '@/entities/quote';
import {
  GuestQuoteDraftMissing,
  GuestQuoteRequestDone,
  GuestQuoteRequestMemberGate,
} from '@/features/guest-quote-request';
import { QuoteRequestUnavailable } from '@/features/quote-request';
import {
  GUEST_QUOTE_REQUEST_COLOR_PARAM,
  GUEST_QUOTE_REQUEST_PRODUCT_PARAM,
  QUOTE_REQUEST_QTY_PARAM,
} from '@/shared/config';
import { EyebrowLabel, SitePageShell } from '@/shared/ui';
import {
  GuestQuoteRequestForm,
  useGuestRequestDraft,
  useGuestRequestReceipt,
} from '@/widgets/guest-quote-request-form';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

interface GuestRequestParams {
  readonly productId: string;
  readonly colorOptionId: string;
  readonly quantity: number | null;
}

/** ?qty= 쿼리 — 양의 정수일 때만 처음 수량으로 넘긴다(최소 주문 수량 확인은 폼이 한다). */
const parseQtyParam = (raw: string | null): number | null => {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
};

/** 상품·색상은 uuid(소문자로 정규화)여야 한다 — 하나라도 아니면 null. */
const parseParams = (search: URLSearchParams): GuestRequestParams | null => {
  const productId = normalizedUuidSchema.safeParse(
    search.get(GUEST_QUOTE_REQUEST_PRODUCT_PARAM) ?? '',
  );
  const colorOptionId = normalizedUuidSchema.safeParse(
    search.get(GUEST_QUOTE_REQUEST_COLOR_PARAM) ?? '',
  );
  if (!productId.success || !colorOptionId.success) return null;
  return {
    productId: productId.data,
    colorOptionId: colorOptionId.data,
    quantity: parseQtyParam(search.get(QUOTE_REQUEST_QTY_PARAM)),
  };
};

/**
 * 비회원 견적 요청 페이지(`GUEST_QUOTE_REQUEST_PATH` = /request/guest?productId=&colorId=&qty=, 비회원 견적 설계 G12·G13) — 공개 라우트.
 * 에디터의 [견적 요청하기]가 비회원이면 임시 작업과 앞면 미리보기 그림을 저장하고 이리로 보낸다. 임시 작업이 없거나 주소가 잘못됐으면 안내만, 요청 불가·준비 중이면
 * 회원 요청 화면과 같은 안내, 요청 정보를 받으면 폼을 붙인다. 로그인한 회원에게는 폼 대신 에디터에서 회원 견적으로 요청하라는 안내를 둔다.
 * 접수되면 임시 작업·미리보기를 지우고 같은 자리에 완료 화면(접수번호·견적 링크)을 둔다 — 접수 결과는 지금 기록 항목의 history state 에도 남아
 * 새로고침·뒤로 가기로 다시 열어도 완료 화면이 나온다. 머리는 회원 견적 요청 화면과 같은 결이다.
 */
export const GuestQuoteRequestPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const params = parseParams(searchParams);
  // 에디터 임시 작업·앞면 미리보기 읽기와 접수 뒤 정리, 접수 결과 보관(history state)은 위젯 훅이 맡는다 — 페이지는 주소 해석과 조합만 한다.
  const { draft, previewSrc, clearAfterSubmit } = useGuestRequestDraft(params);
  const { receipt, keepReceipt } = useGuestRequestReceipt();
  const formQuery = useGuestQuoteRequestFormQuery(
    { productId: params?.productId ?? '', colorOptionId: params?.colorOptionId ?? '' },
    { enabled: draft !== null && receipt === null },
  );

  let body: ReactElement;

  if (receipt !== null) {
    body = <GuestQuoteRequestDone receipt={receipt} />;
  } else if (params === null || draft === null) {
    body = <GuestQuoteDraftMissing productId={params?.productId ?? null} />;
  } else if (formQuery.data !== undefined) {
    body = (
      <GuestQuoteRequestForm
        form={formQuery.data}
        design={draft}
        initialQuantity={params.quantity}
        designPreviewSrc={previewSrc}
        onSubmitted={(next) => {
          clearAfterSubmit();
          keepReceipt(next);
        }}
      />
    );
  } else if (formQuery.isError) {
    body = (
      <QuoteRequestUnavailable
        error={formQuery.error}
        onRetry={() => {
          void formQuery.refetch();
        }}
      />
    );
  } else {
    // 폼과 같은 두 칸 자리표시 — 문구는 화면낭독기에만 읽힌다.
    body = (
      <div>
        <p role="status" className="sr-only">
          견적 요청 정보를 불러오는 중입니다.
        </p>
        <div
          aria-hidden
          className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16"
        >
          <div className="flex flex-col gap-6">
            <div className="h-14 w-full animate-pulse bg-muted/70" />
            <div className="h-72 w-full animate-pulse bg-muted/70" />
          </div>
          <div className="min-h-80 animate-pulse bg-muted/70" />
        </div>
      </div>
    );
  }

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <header className="flex flex-col gap-5 border-b border-foreground/10 pb-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
          <div className="flex flex-col gap-4">
            <EyebrowLabel>Quote Request</EyebrowLabel>
            <h1 className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em]">
              견적 요청
            </h1>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground lg:max-w-xl lg:pb-1 lg:text-right">
            로그인하지 않아도 연락처만 남기면 담당자가 확인해 연락드려요. 확정 견적은 접수 뒤 받는
            견적 링크에서 확인할 수 있어요.
          </p>
        </header>
        {receipt !== null ? (
          body
        ) : (
          <GuestQuoteRequestMemberGate productId={params?.productId ?? null}>
            {body}
          </GuestQuoteRequestMemberGate>
        )}
      </div>
    </SitePageShell>
  );
};
